import { AxiosPromise } from 'axios';
import { instance } from '.';
import { URLS } from './index';
import { Logger } from 'fsts';
import { SearchParams, createSearchParams } from '../model/searchParams';
import { DefaultBackendHelper } from '../utils/backendHelper';
import course, {
  Course,
  CourseTypeEnum,
  CourseTypeFilterElement,
  Value,
  getCourseTypeText,
} from '../model/course';
import ODataFilterBuilder from 'odata-filter-builder';
import { CONST } from '@/shared/utils/constants';

const logger = new Logger('backend.Course');
export interface BackendCourse {
  getCourse(id: string): AxiosPromise<Course>;
  getCourses: (
    searchParams: SearchParams,
    locationIds?: string[],
    departmentId?: string,
    active?: boolean,
    includeEvents?: boolean,
    courseIds?: string[],
    courseTypeFilterElements?: CourseTypeFilterElement[]
  ) => AxiosPromise<Value>;
  getCoursesOdata: (
    locationIds: string[],
    departmentIds: string[],
    companyId: string,
    includeEvents?: boolean
  ) => AxiosPromise<Value>;
  getCoursesWithDateTime: (searchParams: SearchParams) => AxiosPromise<Value>;
  getCoursesWithLogo(payload?: { companyId?: string }): AxiosPromise<Course[]>;
  deleteCourse(id: string): AxiosPromise;
  updateCourse(Course: Course): AxiosPromise<any>;
  getLogoByCourseId(courseId: string): AxiosPromise<Course>;
  makeCopyOfTheCourse(courseId: string): AxiosPromise;
  checkIfCourseHasBookings(courseId: string): AxiosPromise<boolean>;
}

export const defaultBackendCourse: BackendCourse = {
  getCourse(id: string): AxiosPromise<Course> {
    let url = `${URLS.courseOdata}/${id}`;
    return instance.get<Course>(url);
  },
  getCoursesWithLogo(payload?: { companyId?: string }): AxiosPromise<Course[]> {
    const companyId = payload?.companyId || CONST.emptyGuid;
    let url = `${URLS.course}/GetWithLogoOnly`;
    if (companyId !== CONST.emptyGuid) {
      url = `${URLS.course}/GetWithLogoOnlyAnonymous/${companyId}`;
    }
    return instance.get<Course[]>(url);
  },
  getCourses(
    searchParams: SearchParams,
    locationIds?: string[],
    departmentId?: string,
    active?: boolean,
    includeEvents?: boolean,
    courseIds?: string[],
    courseTypeFilterElements?: CourseTypeFilterElement[]
  ): AxiosPromise<Value> {
    const odfb = ODataFilterBuilder('and');

    // Unique event "courses" are hidden in table view
    if (!includeEvents) {
      odfb.ne(CONST.Type, getCourseTypeText(CourseTypeEnum.UniqueEvent));
    }

    if (courseIds != undefined && courseIds.length > 0) {
      const odfbCourses = ODataFilterBuilder('or');
      courseIds.forEach((x) => odfbCourses.eq(CONST.Id, x, false));
      odfb.and(odfbCourses);
    }

    if (active != undefined) {
      odfb.eq(CONST.Active, active);
    }

    if (
      courseTypeFilterElements !== undefined &&
      courseTypeFilterElements.length
    ) {
      const odfbInnerOr = ODataFilterBuilder('or');
      for (const element of courseTypeFilterElements) {
        const odfbInnerAnd = ODataFilterBuilder('and');
        odfbInnerAnd.eq(CONST.Type, getCourseTypeText(element.courseType));
        odfbInnerAnd.eq(CONST.BookableAsSeries, element.seriesCourse);
        odfbInnerOr.or(odfbInnerAnd);
      }
      odfb.and(odfbInnerOr);
    }

    let baseUrl;
    if (departmentId !== undefined) {
      baseUrl = `${URLS.courseOdata}/GetInDepartment(departmentId=${departmentId})`;
    } else if (locationIds !== undefined && locationIds.length > 0) {
      const locationIdsString = locationIds.map((id) => `'${id}'`).join(',');
      baseUrl = `${URLS.courseOdata}/GetInLocations(locationIds=[${locationIdsString}])`;
    } else {
      baseUrl = `${URLS.courseOdata}`;
    }

    const url = DefaultBackendHelper.makeUrl(
      baseUrl,
      searchParams.dataOption,
      searchParams.orClauseFieldsIds,
      searchParams.filter,
      odfb
    );
    logger.log(`getCourses${url}`);
    return instance.get<Value>(url);
  },
  getCoursesOdata(
    locationIds: string[],
    departmentIds: string[],
    companyId: string,
    includeEvents?: boolean
  ): AxiosPromise<Value> {
    var odfb = ODataFilterBuilder('and');

    // Unique event "courses" are hidden in table view
    if (!includeEvents) {
      odfb.ne(CONST.Type, getCourseTypeText(CourseTypeEnum.UniqueEvent));
    }

    var odfbLocations = ODataFilterBuilder('or');
    locationIds.forEach((id) =>
      odfbLocations.or(
        `CourseLocations/any(CourseLocation: CourseLocation/LocationId eq ${id})`
      )
    );

    var odfbDepartments = ODataFilterBuilder('or');
    departmentIds.forEach((id) =>
      odfbDepartments.or(
        `DepartmentRefs/any(DepartmentRef: DepartmentRef/DepartmentId eq ${id})`
      )
    );
    odfb.and(odfbLocations).and(odfbDepartments);

    const defaultUrl =
      companyId === CONST.emptyGuid
        ? `${URLS.course}/GetByOptions`
        : `${URLS.course}/GetByOptionsAnonymous/${companyId}`;
    let url = DefaultBackendHelper.makeUrl(
      defaultUrl,
      createSearchParams(-1, 1, ['name'], [false]).dataOption,
      undefined,
      undefined,
      odfb
    );
    logger.log(`getCourses${url}`);
    return instance.get<Value>(url);
  },
  getCoursesWithDateTime(searchParams: SearchParams): AxiosPromise<Value> {
    let url = DefaultBackendHelper.makeUrl(
      `${URLS.courseOdata}/GetWithDateTime`,
      searchParams.dataOption,
      searchParams.orClauseFieldsIds,
      searchParams.filter
    );
    logger.log(`getCourses${url}`);
    return instance.get<Value>(url);
  },

  deleteCourse(id: string): AxiosPromise {
    logger.debug('deleteCourse');
    return instance.delete(`${URLS.course}/${id}`);
  },
  updateCourse(Course: Course): AxiosPromise<any> {
    logger.debug('updateCourse');
    console.log(Course);
    if (Course.id != '' && Course.id != undefined) {
      return instance.put<Course>(`${URLS.course}`, course.toAPI(Course));
    } else {
      return instance.post<Course>(`${URLS.course}`, course.toAPI(Course));
    }
  },
  getLogoByCourseId(courseId: string): AxiosPromise<Course> {
    logger.debug('getLogoByCourseId');
    return instance.get<Course>(`${URLS.course}/GetLogoByCourseId/${courseId}`);
  },
  makeCopyOfTheCourse(courseId): AxiosPromise {
    logger.debug('copyCourse');
    return instance.post<Course>(`${URLS.course}/CopyCourse`, { Id: courseId });
  },
  checkIfCourseHasBookings(courseId: string): AxiosPromise<boolean> {
    return instance.get<boolean>(
      `${URLS.course}/CheckIfCourseHasBookings/${courseId}`
    );
  },
};
